import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { GenericFormComponentProps } from '../types';
import { makeDateConsistent } from 'helpers/dates';
import formInputStyles from '../shared.module.scss';
import { useEffect } from 'react';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';
import { DesktopDatePickerProps } from '@mui/lab';

type FormDatePickerProps = GenericFormComponentProps &
    DesktopDatePickerProps<Date>;

/**
 * simple form date input
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @returns FormDate component
 */
export function FormDatePicker({
    field,
    label,
    disabled,
    className,
    ...props
}: FormDatePickerProps) {
    const { control, getFieldState } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                label = label || convertToDisplayName(field);
                const id = `text-field-${field}`;
                const formattedDate = makeDateConsistent(fieldProps.value);
                return (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                            {...props}
                            label={label}
                            className={`${formInputStyles.formInput} ${className}`}
                            disabled={disabled}
                            value={formattedDate}
                            onChange={fieldProps.onChange}
                            renderInput={(params) => (
                                <TextField
                                    id={id}
                                    variant="standard"
                                    color={error ? 'error' : 'primary'}
                                    error={!!error}
                                    inputProps={{ ...fieldProps }}
                                    helperText={error?.message}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            // change mm/dd/yy to mm/dd/yyyy. DatePicker reads 01/01/23 as January 1st of 0023 ¯\_(ツ)_/¯
                                            fieldProps.onChange(
                                                new Date(e.target.value)
                                            );
                                        }
                                    }}
                                    {...params}
                                />
                            )}
                        />
                    </LocalizationProvider>
                );
            }}
        />
    );
}
